import React from 'react';

import { IconButton, Checkbox as MuiCheckbox } from '@mui/material';
import { IndentPlugin } from '@platejs/indent/react';
import { isOrderedList, someList, toggleList } from '@platejs/list';
import type { ListStyleType } from '@platejs/list';
import { ListPlugin, useTodoListElement, useTodoListElementState } from '@platejs/list/react';
import clsx from 'clsx';
import type { TElement, TListElement } from 'platejs';
import { KEYS } from 'platejs';
import type { PlateElementProps, RenderNodeWrapper } from 'platejs/react';
import { useEditorRef, useEditorSelector, useReadOnly } from 'platejs/react';
import { AlineTooltip } from 'src/components';
import { makeStyles } from 'tss-react/mui';

import { focusEditor } from '../utils/focus';
import { getToolbarButtonStyles } from '../utils/styles';

const useListStyles = makeStyles()(theme => ({
  list: {
    margin: 0,
    padding: 0,
    paddingInlineStart: theme.spacing(3),
    position: 'relative',
  },
  todoCheckbox: {
    left: -4,
    padding: 0,
    position: 'absolute',
    top: 0,
  },
  todoLi: {
    listStyle: 'none',
  },
  // Toolbar button styles (shared)
  ...getToolbarButtonStyles(theme),
}));

const TodoMarker = React.memo(function TodoMarker(props: PlateElementProps): JSX.Element {
  const readOnly = useReadOnly();
  const { classes } = useListStyles();
  const state = useTodoListElementState({ element: props.element });
  const { checkboxProps } = useTodoListElement(state);

  return (
    <div contentEditable={false}>
      <MuiCheckbox
        checked={checkboxProps.checked}
        className={classes.todoCheckbox}
        disabled={readOnly}
        size="small"
        onChange={e => checkboxProps.onCheckedChange(e.target.checked)}
        onMouseDown={checkboxProps.onMouseDown}
      />
    </div>
  );
});

const TodoLi = React.memo(function TodoLi(props: PlateElementProps): JSX.Element {
  const { classes } = useListStyles();

  return <li className={classes.todoLi}>{props.children}</li>;
});

const config: Record<
  string,
  {
    Li: React.FC<PlateElementProps>;
    Marker: React.FC<PlateElementProps>;
  }
> = {
  todo: {
    Li: TodoLi,
    Marker: TodoMarker,
  },
};

const List = React.memo(function List(props: PlateElementProps): JSX.Element {
  const { classes } = useListStyles();
  const { indent = 1, listStart, listStyleType } = props.element as TListElement;
  const { Li, Marker } = config[listStyleType] ?? {};
  const ListTag = isOrderedList(props.element) ? 'ol' : 'ul';
  // Left margin for nested lists: indent=1 has no margin, indent=2+ adds 24px per level
  const indentOffset = Math.max(0, indent - 1) * 24;

  return (
    <ListTag
      className={classes.list}
      start={listStart}
      style={{
        listStyleType: listStyleType,
        marginLeft: indentOffset,
      }}
    >
      {Marker && <Marker {...props} />}
      {Li ? <Li {...props} /> : <li>{props.children}</li>}
    </ListTag>
  );
});

const BlockList: RenderNodeWrapper = props => {
  if (!props.element.listStyleType) return;

  const ListWrapper = ({ element, ...rest }: PlateElementProps): JSX.Element | null => {
    if (!getIsListElement(element)) return null;
    return <List {...rest} element={element} />;
  };

  return ListWrapper;
};

/** List toolbar button for toggling list types */
export const ListToolbarButton = React.forwardRef<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  any,
  {
    children: React.ReactNode;
    listStyleType: ListStyleType | string;
    tooltip: string;
    afterClick?: () => void;
    disabled?: boolean;
  }
>(function ListToolbarButton(
  { afterClick, children, disabled, listStyleType, tooltip },
  ref
): JSX.Element {
  const editor = useEditorRef();
  const { classes } = useListStyles();
  const pressed = useEditorSelector(editor => someList(editor, [listStyleType]), [listStyleType]);
  const isPressed = !disabled && pressed;

  return (
    <AlineTooltip arrow placement="top" title={disabled ? '' : tooltip}>
      <IconButton
        ref={ref}
        aria-label={tooltip}
        className={clsx(
          classes.toolbarButton,
          isPressed ? classes.toolbarButtonPressed : classes.toolbarButtonDefault
        )}
        disabled={disabled}
        size="small"
        onClick={() => {
          toggleList(editor, { listStyleType });
          focusEditor(editor);
          afterClick?.();
        }}
      >
        {children}
      </IconButton>
    </AlineTooltip>
  );
});

/** Type guard to check if an element is a list element */
export function getIsListElement(element: TElement): element is TListElement {
  return element.listStyleType !== undefined;
}

/** Configure IndentPlugin with target plugins that can be indented */
export const IndentKit = [
  IndentPlugin.configure({
    inject: {
      targetPlugins: [...KEYS.heading, KEYS.p, KEYS.blockquote],
    },
  }).overrideEditor(({ editor, tf: { tab } }) => ({
    transforms: {
      /**
       * Tab/Shift+Tab only nest and un-nest list items; in any other block we let the transform
       * return false so the core Tab handler doesn't `preventDefault`, and the keypress moves focus
       * out of the editor as expected. Without this guard IndentPlugin indents every paragraph and
       * the composer traps keyboard focus.
       */
      tab(options) {
        const entry = editor.api.block();
        if (entry && getIsListElement(entry[0])) return tab(options);
        return false;
      },
    },
  })),
];

/** Configure ListPlugin with IndentPlugin and custom rendering */
export const ListKit = [
  ...IndentKit,
  ListPlugin.configure({
    inject: {
      targetPlugins: [...KEYS.heading, KEYS.p, KEYS.blockquote],
    },
    render: {
      belowNodes: BlockList,
    },
  }),
];
